/**
 * ピロー簡易診断（<pillow-diagnosis>）
 *
 * snippets/pillow-diagnosis-content.liquid のマークアップを制御する Web Component。
 * 商品LPのポップアップ（remodal 内）と固定ページの両方で同じコードが動く。
 *
 * - 入力値（寝姿勢・性別・身長・体重・年齢・骨格タイプ）はコンポーネント内の
 *   メモリ上でのみ扱い、送信・保存は一切行わない
 * - 診断ロジック（スコアリング）は旗艦店フィッティング知見ベースの仕様値。
 *   変更する場合は compute / buildScore / ageAdj をセットで見直すこと
 */

/* 計測（GTM の dataLayer へ送る）。GA4 側は GTM でカスタムイベント diagnosis_* を受けて送信する。
   身長・体重・年齢などの入力値は送らない。送るのは設問番号・推奨サイズ・押したボタンだけ */
const pillowDiagnosisTrack = (eventName, params) => {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName, diagnosis_name: 'pillow_height' }, params || {}));
  } catch (error) {
    // 計測の失敗で診断を止めない
  }
};

const PILLOW_DIAGNOSIS_POSTURE_JA = { back: '仰向け', side: '横向き', front: 'うつ伏せ' };

const PILLOW_DIAGNOSIS_REASON = {
  back: '仰向け寝は首の自然なカーブを保てる、<b>高すぎない高さ</b>が適しています。',
  front: 'うつ伏せ寝は胸や首への圧迫を避けるため、<b>高すぎない標準的な高さ</b>が適しています。',
  side: '横向き寝は肩の分だけ頭とマットレスの間に空間ができるため、体格に応じた高さが必要です。',
};

const PILLOW_DIAGNOSIS_SIZE_JA = {
  LOW: 'ロー（低め）サイズ',
  STANDARD: 'スタンダードサイズ',
  HIGH: 'ハイ（高め）サイズ',
};

class PillowDiagnosis extends HTMLElement {
  connectedCallback() {
    // remodal は初期化時に要素を wrapper へ移動するため connectedCallback が再発火する。
    // イベントの二重バインドを防ぐ
    if (this.pdInitialized) return;
    this.pdInitialized = true;

    this.state = this.defaultState();
    this.isModal = this.classList.contains('pillow-diagnosis--modal');
    this.lastResultSize = null;
    this.currentScreen = 'intro';
    this.context = this.isModal ? 'modal' : 'page';

    this.slider = this.querySelector('[data-pd-slider]');
    this.heightVal = this.querySelector('[data-pd-height-val]');
    this.weightInput = this.querySelector('[data-pd-weight]');
    this.ageInput = this.querySelector('[data-pd-age]');

    this.bindNavigation();
    this.bindOptGroups();
    this.bindSexSegment();
    this.bindSlider();
    this.bindOptionalInputs();
    this.bindResultActions();
    this.paintSlider();
  }

  defaultState() {
    return {
      posture: null,
      sex: null,
      height: 165,
      weight: null,
      age: null,
      shoulder: 0,
      thickness: 0,
      pref: null,
    };
  }

  /* ---------- 画面遷移 ---------- */

  goTo(screenName) {
    this.trackTransition(this.currentScreen, screenName);
    this.currentScreen = screenName;
    this.querySelectorAll('[data-pd-screen]').forEach((screen) => {
      screen.classList.toggle('is-active', screen.dataset.pdScreen === screenName);
    });
    this.scrollIntoView({ block: 'start' });
    const active = this.querySelector(`[data-pd-screen="${screenName}"]`);
    const focusTarget = active ? active.querySelector('[data-pd-focus]') : null;
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  }

  // 画面遷移から「開始」と「何問目まで答えたか」を数える（もどる操作は数えない）
  trackTransition(from, to) {
    if (from === 'intro' && to === 'q1') {
      pillowDiagnosisTrack('diagnosis_start', { diagnosis_context: this.context });
      return;
    }
    const fromMatch = /^q(\d)$/.exec(from || '');
    const toMatch = /^q(\d)$/.exec(to || '');
    if (fromMatch && toMatch && Number(toMatch[1]) === Number(fromMatch[1]) + 1) {
      pillowDiagnosisTrack('diagnosis_answer', {
        diagnosis_context: this.context,
        question_number: Number(fromMatch[1]),
        question_total: 4,
      });
    }
  }

  enableNext(questionName) {
    const nextButton = this.querySelector(`[data-pd-next="${questionName}"]`);
    if (nextButton) nextButton.disabled = false;
  }

  bindNavigation() {
    this.querySelectorAll('[data-pd-goto]').forEach((button) => {
      button.addEventListener('click', () => this.goTo(button.dataset.pdGoto));
    });
  }

  /* ---------- 選択肢（寝姿勢・好み・骨格タイプ） ---------- */

  bindOptGroup(groupName, onSelect) {
    const group = this.querySelector(`[data-pd-opts="${groupName}"]`);
    if (!group) return;
    group.querySelectorAll('.pillow-diagnosis__opt').forEach((button) => {
      button.addEventListener('click', () => {
        group.querySelectorAll('.pillow-diagnosis__opt').forEach((other) => {
          other.classList.remove('is-selected');
          other.setAttribute('aria-pressed', 'false');
        });
        button.classList.add('is-selected');
        button.setAttribute('aria-pressed', 'true');
        onSelect(button.dataset.pdValue);
      });
    });
  }

  bindOptGroups() {
    this.bindOptGroup('posture', (value) => {
      this.state.posture = value;
      this.enableNext('q1');
    });
    this.bindOptGroup('pref', (value) => {
      this.state.pref = value;
      this.enableNext('q3');
    });
    this.bindOptGroup('shoulder', (value) => {
      this.state.shoulder = parseInt(value, 10);
    });
    this.bindOptGroup('thickness', (value) => {
      this.state.thickness = parseInt(value, 10);
    });
  }

  bindSexSegment() {
    const segment = this.querySelector('[data-pd-seg="sex"]');
    if (!segment) return;
    segment.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', () => {
        segment.querySelectorAll('button').forEach((other) => {
          other.classList.remove('is-selected');
          other.setAttribute('aria-pressed', 'false');
        });
        button.classList.add('is-selected');
        button.setAttribute('aria-pressed', 'true');
        this.state.sex = button.dataset.pdValue;
        this.enableNext('q2');
      });
    });
  }

  /* ---------- 身長スライダー ---------- */

  paintSlider() {
    if (!this.slider) return;
    const min = Number(this.slider.min);
    const max = Number(this.slider.max);
    const value = Number(this.slider.value);
    const percent = ((value - min) / (max - min)) * 100;
    this.slider.style.setProperty('--pd-fill', `${percent}%`);
    if (this.heightVal) this.heightVal.textContent = this.slider.value;
    this.state.height = value;
  }

  bindSlider() {
    if (!this.slider) return;
    this.slider.addEventListener('input', () => this.paintSlider());
    this.querySelectorAll('[data-pd-step]').forEach((button) => {
      button.addEventListener('click', () => {
        const delta = parseInt(button.dataset.pdStep, 10);
        const min = Number(this.slider.min);
        const max = Number(this.slider.max);
        this.slider.value = Math.max(min, Math.min(max, Number(this.slider.value) + delta));
        this.paintSlider();
      });
    });
  }

  /* ---------- 体重・年齢（任意） ---------- */

  bindOptionalInputs() {
    if (this.weightInput) {
      this.weightInput.addEventListener('input', (event) => {
        const value = parseFloat(event.target.value);
        this.state.weight = value >= 30 && value <= 150 ? value : null;
      });
    }
    if (this.ageInput) {
      this.ageInput.addEventListener('input', (event) => {
        const value = parseInt(event.target.value, 10);
        this.state.age = value >= 10 && value <= 110 ? value : null;
      });
    }
  }

  resetOptionalBlock() {
    this.state.shoulder = 0;
    this.state.thickness = 0;
    this.state.weight = null;
    this.state.age = null;
    if (this.weightInput) this.weightInput.value = '';
    if (this.ageInput) this.ageInput.value = '';
    ['shoulder', 'thickness'].forEach((groupName) => {
      const group = this.querySelector(`[data-pd-opts="${groupName}"]`);
      if (!group) return;
      group.querySelectorAll('.pillow-diagnosis__opt').forEach((button) => {
        button.classList.remove('is-selected');
        button.setAttribute('aria-pressed', 'false');
      });
      const neutral = group.querySelector('[data-pd-value="0"]');
      if (neutral) {
        neutral.classList.add('is-selected');
        neutral.setAttribute('aria-pressed', 'true');
      }
    });
  }

  /* ---------- スコアリング（プロトタイプ仕様を変更なしで移植） ----------
     姿勢点: back 0 / front +1 / side +2
     体格点(横向きのみ): 男 178+:+2, 168-177:+1 / 女 170+:+2, 158-169:+1
       肩のつくり: 張り+1 / 中立0 / なだらか-1
       厚み系補正: 体重入力あり→BMI(>=27:+1 / <18.5:-1) / なし→厚み(+1/0/-1)
       補正後0-2にクランプ
     年齢補正: 60歳以上 → +0.5 (全姿勢)
     指向点: low -1 / mid 0 / high +1
     最終スコアは切り捨て(保守側=HIGH抑制を維持)
     判定(交換サービス前提・STANDARD中心):
       <=-1 LOW / 0-3 STANDARD / 4 STANDARD+sheet / 5 HIGH
       ※アジャストシートは「高さを足す」商品のため、シート提案はSTANDARD上側境界のみ。
         下側(低すぎ懸念)はシートでは解決できず、交換サービスで担保する。 */

  bmi() {
    if (!this.state.weight) return null;
    return this.state.weight / Math.pow(this.state.height / 100, 2);
  }

  buildScore() {
    if (this.state.posture !== 'side') return 0;
    const thresholds = this.state.sex === 'male' ? [168, 178] : [158, 170];
    let score = 0;
    if (this.state.height >= thresholds[1]) score = 2;
    else if (this.state.height >= thresholds[0]) score = 1;
    const bmiValue = this.bmi();
    if (bmiValue !== null) {
      if (bmiValue >= 27) score += 1;
      else if (bmiValue < 18.5) score -= 1;
    } else {
      score += this.state.thickness;
    }
    score += this.state.shoulder;
    return Math.max(0, Math.min(2, score));
  }

  ageAdj() {
    return this.state.age !== null && this.state.age >= 60 ? 0.5 : 0;
  }

  compute() {
    const posture = { back: 0, front: 1, side: 2 }[this.state.posture];
    const pref = { low: -1, mid: 0, high: 1 }[this.state.pref];
    const score = Math.floor(posture + this.buildScore() + pref + this.ageAdj());
    if (score <= -1) return { size: 'LOW', sheet: false };
    if (score <= 3) return { size: 'STANDARD', sheet: false };
    if (score === 4) return { size: 'STANDARD', sheet: true };
    return { size: 'HIGH', sheet: false };
  }

  /* ---------- 診断結果 ---------- */

  buildReason(result) {
    let reason = `<b>${PILLOW_DIAGNOSIS_POSTURE_JA[this.state.posture]}寝</b>のあなたへ。${PILLOW_DIAGNOSIS_REASON[this.state.posture]}`;
    if (result.size === 'STANDARD' && this.state.posture === 'back') {
      reason += '交換サービスで上下どちらにも変更できるため、まずは中心サイズのSTANDARDをおすすめしています。';
    }
    if (this.state.posture === 'side') {
      const buildScore = this.buildScore();
      if (buildScore >= 2) reason += 'あなたの体格では肩幅が広めと推定されるため、しっかりした高さをおすすめします。';
      else if (buildScore === 1) reason += 'あなたの体格では標準的な肩幅と推定されます。';
      else reason += 'あなたの体格では肩幅が小さめと推定されるため、高すぎない枕が合います。';
    }
    if (this.state.posture === 'side' && (this.bmi() !== null || this.state.shoulder !== 0 || this.state.thickness !== 0)) {
      reason += '骨格タイプの情報も含めて体格を推定しています。';
    }
    if (this.ageAdj() > 0) reason += '年齢による姿勢の変化も考慮しています。';
    return reason;
  }

  showResult(inputMode) {
    const result = this.compute();
    pillowDiagnosisTrack('diagnosis_complete', {
      diagnosis_context: this.context,
      result_size: result.size,
      result_sheet: result.sheet ? 'yes' : 'no',
      input_mode: inputMode || 'detail',
    });

    this.querySelector('[data-pd-result-size]').textContent = result.size;
    this.querySelector('[data-pd-result-sub]').textContent = PILLOW_DIAGNOSIS_SIZE_JA[result.size];
    this.querySelector('[data-pd-reason]').innerHTML = this.buildReason(result);

    this.querySelectorAll('[data-pd-gauge]').forEach((segment) => {
      segment.classList.remove('is-hit', 'is-adj');
    });
    const hitKey = { LOW: 'low', STANDARD: 'std', HIGH: 'high' }[result.size];
    this.querySelector(`[data-pd-gauge="${hitKey}"]`).classList.add('is-hit');

    // アジャストシート案内（__xsell）は全サイズ共通の静的マークアップ（snippet 側）に統一。
    // シート提案時（STANDARD 上側境界）はゲージの表示のみ切り替える
    const gaugeCap = this.querySelector('[data-pd-gauge-cap]');
    if (result.sheet) {
      this.querySelector('[data-pd-gauge="high"]').classList.add('is-adj');
      gaugeCap.innerHTML =
        '<span class="pillow-diagnosis__swatch pillow-diagnosis__swatch--crimson"></span>おすすめサイズ　'
        + '<span class="pillow-diagnosis__swatch pillow-diagnosis__swatch--blue"></span>アジャストシートでカバーできる高さ';
    } else {
      gaugeCap.innerHTML = '<span class="pillow-diagnosis__swatch pillow-diagnosis__swatch--crimson"></span>おすすめサイズ';
    }

    const cta = this.querySelector('[data-pd-cta]');
    this.lastResultSize = result.size;
    if (this.isModal) {
      cta.textContent = `${result.size}サイズを選択する`;
    } else {
      // 固定ページ文脈: 別タブで商品ページを開き、推奨サイズを ?variant= で事前選択させる
      cta.textContent = `${result.size}サイズの商品ページへ`;
      cta.href = this.buildVariantUrl(result.size);
    }

    this.goTo('result');
  }

  buildVariantUrl(size) {
    // バリアントIDは snippet 側が all_products['pillow'] から解決して
    // data-pd-variant-* 属性で渡す。取得できない場合は商品ページURLのみで遷移
    const base = this.dataset.pdProductUrl || '/products/pillow';
    const attrKey = { LOW: 'pdVariantLow', STANDARD: 'pdVariantStandard', HIGH: 'pdVariantHigh' }[size];
    const variantId = attrKey ? this.dataset[attrKey] : null;
    if (variantId) return `${base}?variant=${variantId}`;
    return base;
  }

  restart() {
    this.currentScreen = null; // 結果からのやり直しを intro→q1 の「開始」として数え直す
    this.state = this.defaultState();
    this.querySelectorAll('[data-pd-opts="posture"] .pillow-diagnosis__opt, [data-pd-opts="pref"] .pillow-diagnosis__opt, [data-pd-seg] button').forEach((button) => {
      button.classList.remove('is-selected');
      button.setAttribute('aria-pressed', 'false');
    });
    this.resetOptionalBlock();
    if (this.slider) {
      this.slider.value = 165;
      this.paintSlider();
    }
    this.querySelectorAll('[data-pd-next]').forEach((button) => {
      button.disabled = true;
    });
    this.goTo('intro');
  }

  bindResultActions() {
    const showResultButton = this.querySelector('[data-pd-show-result]');
    if (showResultButton) showResultButton.addEventListener('click', () => this.showResult('detail'));

    const skipButton = this.querySelector('[data-pd-skip]');
    if (skipButton) {
      skipButton.addEventListener('click', () => {
        this.resetOptionalBlock();
        this.showResult('skip');
      });
    }

    // 結果画面のリンク・ボタンの押下（押した場所は cta で区別）
    const resultScreen = this.querySelector('[data-pd-screen="result"]');
    if (resultScreen) {
      resultScreen.addEventListener('click', (event) => {
        const el = event.target.closest('a, button');
        if (!el) return;
        let cta = null;
        if (el.matches('[data-pd-cta]')) cta = this.isModal ? 'size_select' : 'product_page';
        else if (el.matches('[data-pd-restart]')) cta = 'restart';
        else if (el.classList.contains('pillow-diagnosis__xsell-link')) cta = 'adjust_sheet';
        else if (el.classList.contains('pillow-diagnosis__store-link')) cta = 'store';
        if (!cta) return;
        pillowDiagnosisTrack('diagnosis_cta_click', {
          diagnosis_context: this.context,
          cta,
          result_size: this.lastResultSize || '',
          link_url: el.tagName === 'A' ? el.getAttribute('href') : '',
        });
      }, true);
    }

    const restartButton = this.querySelector('[data-pd-restart]');
    if (restartButton) restartButton.addEventListener('click', () => this.restart());

    const cta = this.querySelector('[data-pd-cta]');
    if (cta && this.isModal) {
      // モーダル文脈（＝すでに商品ページにいる）ではページ遷移せず、
      // モーダルを閉じてサイズ選択へスクロール＋推奨サイズをベストエフォートで選択する
      cta.addEventListener('click', (event) => {
        event.preventDefault();
        this.applyResultToProductPage();
      });
    }
  }

  applyResultToProductPage() {
    const size = this.lastResultSize;
    const modal = this.closest('.remodal');
    // 商品ページのサイズ radio（snippets/product-variant-options.liquid が
    // name="サイズ" value="LOW|STANDARD|HIGH" で描画）をベストエフォートで選択
    const applySelection = () => {
      const radio = size
        ? document.querySelector(`input[type="radio"][name="サイズ"][value="${size}"]`)
        : null;
      if (radio && !radio.disabled && !radio.checked) radio.click();
      const scrollTarget = radio ? radio.closest('fieldset') : document.querySelector('variant-radios, variant-selects');
      if (scrollTarget) scrollTarget.scrollIntoView({ block: 'center' });
    };
    if (modal && window.jQuery) {
      let deferred = false;
      try {
        // クローズアニメーション中に背景がスクロールしないよう、閉じ終えてから選択・スクロールする
        const instance = window.jQuery(modal).remodal();
        window.jQuery(modal).one('closed', applySelection);
        deferred = true;
        instance.close();
        return;
      } catch (error) {
        // remodal 未初期化や close 失敗時はワンショットを解除して即時実行にフォールバック
        if (deferred) window.jQuery(modal).off('closed', applySelection);
      }
    }
    applySelection();
  }

  /* ---------- モーダル文脈のフォーカストラップ ----------
     assets/global.js の共通 trapFocus は getFocusableElements が display:none の
     要素を除外しないため、本コンポーネントのような多画面ウィザード（非アクティブ
     画面が display:none）では first/last の計算が壊れてフォーカスがモーダル外へ
     抜ける。そのため可視要素のみを対象にしたトラップをコンポーネント側で実装する。
     Escape・オーバーレイクリックでの close は remodal 標準機能に任せる */

  getVisibleFocusables(container) {
    const selector = "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe";
    return Array.from(container.querySelectorAll(selector)).filter((element) => element.offsetParent !== null);
  }

  handleTrapKeydown = (event) => {
    if (event.key !== 'Tab') return;
    const modal = this.closest('.remodal');
    if (!modal) return;
    const focusables = this.getVisibleFocusables(modal);
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === modal)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  armFocusTrap() {
    const modal = this.closest('.remodal');
    if (!modal) return;
    modal.addEventListener('keydown', this.handleTrapKeydown);
    const focusTarget = this.querySelector('[data-pd-screen].is-active [data-pd-focus]');
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  }

  releaseFocusTrap(opener) {
    const modal = this.closest('.remodal');
    if (modal) modal.removeEventListener('keydown', this.handleTrapKeydown);
    // preventScroll: 結果CTA経由のクローズでは applySelection のサイズ選択欄への
    // スクロールを打ち消さないようにする（opener は通常すでに画面内にある）
    if (opener && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
  }
}

if (!customElements.get('pillow-diagnosis')) {
  customElements.define('pillow-diagnosis', PillowDiagnosis);
}

/* ---------- 診断バナーの配置 ----------
   バナー（.pillow-diagnosis-banner）は sections/main-product.liquid が LP 本文冒頭に
   描画するが、表示位置は商品説明 body_html 内のサポートリンク（.rn__support__link、
   トライアルサービス案内）の直上。body_html は管理画面管理のため Liquid では
   挿し込めず、ここで DOM 移動する。

   body_html は管理画面側でいつでも構造が変わりうる（コードレビューを通らない）ため、
   目印の解決は防御的に行う:
   - .rn__support__link の fadeIn / is-show は表示アニメーションの状態クラスのため
     セレクタに含めない
   - 同クラスが複数置かれた場合に備え、トライアル案内（href に pillow_trial）行きを優先
   - 目印が <p> 内に移された場合は段落ごと直上に挿入し、<p> 内に <div> が入る
     不正な入れ子を避ける（旧目印と同じ closest('p') パターン）
   目印が見つからない場合は旧目印（#size 内の「サイズ交換１回無料」案内）に
   フォールバックし、それも無ければ元の位置のまま表示する */
(() => {
  const placeBanner = () => {
    const banner = document.querySelector('.pillow-diagnosis-banner');
    if (!banner) return;
    const supportLink =
      document.querySelector('.rn__support__link[href*="pillow_trial"]') ||
      document.querySelector('.rn__support__link');
    const supportAnchor = supportLink ? (supportLink.closest('p') || supportLink) : null;
    if (supportAnchor && supportAnchor.parentElement) {
      supportAnchor.parentElement.insertBefore(banner, supportAnchor);
    } else {
      const exchangeLink = document.querySelector('#size .faq_koukan_link');
      const exchangeNote = exchangeLink ? exchangeLink.closest('p') : null;
      if (exchangeNote && exchangeNote.parentElement) {
        exchangeNote.parentElement.insertBefore(banner, exchangeNote);
      }
    }
    banner.classList.add('is-placed');
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', placeBanner);
  } else {
    placeBanner();
  }
})();

/* ---------- remodal 開閉イベントとフォーカストラップの接続 ---------- */
(() => {
  if (!window.jQuery) return;
  let pillowDiagnosisOpener = null;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-remodal-target="pillow-diagnosis"]');
    if (trigger) {
      pillowDiagnosisOpener = trigger;
      pillowDiagnosisTrack('diagnosis_open', { diagnosis_context: 'modal' });
    }
  });

  window.jQuery(document).on('opened', '.pillow-diagnosis-modal', function () {
    const component = this.querySelector('pillow-diagnosis');
    if (component && typeof component.armFocusTrap === 'function') component.armFocusTrap();
  });

  window.jQuery(document).on('closed', '.pillow-diagnosis-modal', function () {
    const component = this.querySelector('pillow-diagnosis');
    if (component && typeof component.releaseFocusTrap === 'function') component.releaseFocusTrap(pillowDiagnosisOpener);
    pillowDiagnosisOpener = null;
  });
})();
